import React from "react";
import { twMerge } from "tailwind-merge";
import { COVER_GRADIENTS, getCoverGradient } from "../../lib/coverGradients";

/**
 * Quiet card surface — DemoLandingCard language.
 * Use interactive for hover lift; hero for night cover shells.
 */
export default function Card({
  children,
  className = "",
  interactive = false,
  hero = false,
  color,
  style,
  ...rest
}) {
  const useCoverGradient = Boolean(color && COVER_GRADIENTS[color]);
  const coverGradient = useCoverGradient ? getCoverGradient(color) : null;

  const palette = {
    white: "bg-surface",
    cream: "bg-cream",
    muted: "bg-canvas",
    ink: "bg-night text-cream",
    night: "bg-night text-cream",
    primary: "bg-night text-cream",
    accent: "bg-accent text-cream",
    // Semantic pastels — prefer Badge for roles; kept for cover-less tinted blocks
    pink: "bg-pinky/40",
    leaders: "bg-leaders/50",
    followers: "bg-followers/60",
    purple: "bg-leaders/50",
    amber: "bg-leaders/50",
    sky: "bg-followers/60",
  };

  if (hero) {
    return (
      <div
        className={twMerge(
          "rounded-card overflow-hidden border border-white/6 shadow-hero",
          useCoverGradient ? "" : palette[color] || palette.night,
          className,
        )}
        style={useCoverGradient ? { background: coverGradient.background, ...style } : style}
        {...rest}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "quiet-card bg-surface",
        interactive && "quiet-card-interactive",
        !useCoverGradient && (palette[color] || ""),
        className,
      )}
      style={useCoverGradient ? { background: coverGradient.background, ...style } : style}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ children, className = "", ...rest }) {
  return (
    <div className={twMerge("px-4 pt-4 pb-2", className)} {...rest}>
      {children}
    </div>
  );
}

export function CardBody({ children, className = "", ...rest }) {
  return (
    <div className={twMerge("px-4 py-3", className)} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({ children, className = "", ...rest }) {
  return (
    <div
      className={twMerge("border-t border-subtle px-4 py-3", className)}
      {...rest}
    >
      {children}
    </div>
  );
}
